import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { PLAN_OPTIONS, ROLE_OPTIONS } from './shared';

export const AdminEditDialog = ({ editing, setEditing, onClose, onSave, saving, isSuper }) => (
  <Dialog open={!!editing} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="bg-zinc-950 border-white/10" data-testid="admin-edit-dialog">
      <DialogHeader>
        <DialogTitle className="font-heading">Edit {editing?.name}</DialogTitle>
        <DialogDescription className="text-xs text-muted-foreground">{editing?.email}</DialogDescription>
      </DialogHeader>
      {editing && (
        <div className="space-y-4 pt-2">
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider">Plan</label>
            <Select value={editing._plan} onValueChange={(v) => setEditing({ ...editing, _plan: v })}>
              <SelectTrigger className="mt-1.5 bg-zinc-900/60 border-white/10" data-testid="admin-plan-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PLAN_OPTIONS.map(p => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider">Role</label>
            <Select
              value={editing._role}
              onValueChange={(v) => setEditing({ ...editing, _role: v })}
              disabled={!isSuper}
            >
              <SelectTrigger className="mt-1.5 bg-zinc-900/60 border-white/10" data-testid="admin-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map(r => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {!isSuper && (
              <p className="text-xs text-muted-foreground mt-1">Only super admins can change roles.</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider">Monthly credits</label>
              <Input
                type="number"
                min={0}
                value={editing._monthly}
                onChange={(e) => setEditing({ ...editing, _monthly: e.target.value })}
                className="mt-1.5 bg-zinc-900/60 border-white/10"
                data-testid="admin-monthly-credits-input"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider">Purchased credits</label>
              <Input
                type="number"
                min={0}
                value={editing._purchased}
                onChange={(e) => setEditing({ ...editing, _purchased: e.target.value })}
                className="mt-1.5 bg-zinc-900/60 border-white/10"
                data-testid="admin-purchased-credits-input"
              />
            </div>
          </div>
        </div>
      )}
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={onSave} disabled={saving} data-testid="admin-save-user-button">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          Save changes
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);


export const AdminDeleteDialog = ({ deleting, onCancel, onConfirm }) => (
  <Dialog open={!!deleting} onOpenChange={(o) => !o && onCancel()}>
    <DialogContent className="bg-zinc-950 border-white/10">
      <DialogHeader>
        <DialogTitle className="font-heading">Delete user?</DialogTitle>
        <DialogDescription className="text-sm text-muted-foreground">
          This permanently removes <span className="text-foreground font-medium">{deleting?.email}</span>.
          Their videos and generations will remain in the database but become orphaned.
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button variant="destructive" onClick={onConfirm} data-testid="admin-confirm-delete-button">
          Delete user
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);


export const AdminInviteDialog = ({
  open, onClose, isSuper,
  inviteEmail, setInviteEmail,
  inviteRole, setInviteRole,
  createdInvite, setCreatedInvite,
  inviting, onSubmit, onCopyLink,
}) => (
  <Dialog open={open} onOpenChange={(o) => { if (!o) { onClose(); setCreatedInvite(null); } }}>
    <DialogContent className="bg-zinc-950 border-white/10" data-testid="admin-invite-dialog">
      <DialogHeader>
        <DialogTitle className="font-heading">Invite a teammate</DialogTitle>
        <DialogDescription className="text-sm text-muted-foreground">
          {createdInvite
            ? 'Share this link with your teammate. They will set their own password.'
            : 'They will receive admin privileges based on the role you assign. Only super admins can grant admin roles.'}
        </DialogDescription>
      </DialogHeader>

      {createdInvite ? (
        <div className="space-y-3 pt-2">
          <div className="p-3 rounded-lg bg-zinc-900/60 border border-white/10 break-all">
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">Invite link</p>
            <p className="font-mono text-xs leading-relaxed" data-testid="admin-invite-link-display">{createdInvite.accept_url}</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => onCopyLink(createdInvite.accept_url)} className="flex-1" data-testid="admin-invite-copy-link-button">
              Copy link
            </Button>
            <Button variant="ghost" onClick={() => setCreatedInvite(null)} data-testid="admin-invite-new-button">
              Send another
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Expires {new Date(createdInvite.expires_at).toLocaleString()}. Email delivery isn't configured yet — share the link directly.
          </p>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4 pt-2">
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Email</label>
            <Input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="teammate@yourcompany.com"
              className="mt-1.5 bg-zinc-900/60 border-white/10"
              required
              data-testid="admin-invite-email-input"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Role</label>
            <Select value={inviteRole} onValueChange={setInviteRole}>
              <SelectTrigger className="mt-1.5 bg-zinc-900/60 border-white/10" data-testid="admin-invite-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="user">User</SelectItem>
                {isSuper && <SelectItem value="admin">Admin</SelectItem>}
                {isSuper && <SelectItem value="super_admin">Super Admin</SelectItem>}
              </SelectContent>
            </Select>
            {!isSuper && (
              <p className="text-xs text-muted-foreground mt-1">Only super admins can invite admins.</p>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={inviting} data-testid="admin-invite-submit-button">
              {inviting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Create invite
            </Button>
          </DialogFooter>
        </form>
      )}
    </DialogContent>
  </Dialog>
);
